<template>
  <div>
    <v-app-bar
      app
      flat
      outlined
      class="appbar"
    >
      <v-app-bar-nav-icon @click="$router.push('/')">
        <LawLabIcon class="app-icon ml-1" />
      </v-app-bar-nav-icon>

      <v-toolbar-title class="pl-0">
        <router-link
          to="/"
          class="font-weight-bold title-bar d-none d-md-inline"
        >
          LAWLAB
        </router-link>
        <span class="text-caption disabled ml-2">v{{ appTag }}-{{ appHash }}</span>
      </v-toolbar-title>

      <v-spacer />

      <transition
        v-if="!currentUser.isExternal"
        name="fade"
      >
        <v-form
          ref="form"
          v-model="search.formValid"
          accept-charset="UTF-8"
          action="/search"
          class="mt-6 ml-4 d-none d-sm-inline"
          method="get"
          style="max-width: 500px; width: 100%;"
          @submit="checkSearchForm"
        >
          <v-text-field
            v-model="search.term"
            append-icon="search"
            dense
            label="Global Search"
            name="term"
            :rules="search.formRules"
            single-line
            solo
            text
            @focusout="resetFormValidation"
          />
        </v-form>
      </transition>

      <v-divider
        vertical
        class="ml-7 mr-3"
      />

      <TmetricBox />

      <NotificationsBox />

      <v-btn
        v-if="currentUser.isAdmin"
        icon
        title="Open Admin Menu"
        @click="goToSettings"
      >
        <v-icon>settings</v-icon>
      </v-btn>

      <CalendarSyncStatusButton />

      <v-divider
        vertical
        class="mx-5"
      />

      <v-menu
        transition="scroll-y-reverse-transition"
        offset-y
        :close-on-content-click="false"
      >
        <template #activator="{ on }">
          <v-btn
            icon
            class="mr-2"
            v-on="on"
          >
            <v-badge
              bordered
              bottom
              color="light-green darken-1"
              dot
              offset-x="10"
              offset-y="10"
            >
              <v-avatar
                color="secondary"
                size="40"
              >
                <span class="white--text">{{ avatar }}</span>
              </v-avatar>
            </v-badge>
          </v-btn>
        </template>

        <v-list>
          <v-list>
            <v-list-item>
              <v-list-item-content class="py-0">
                <v-list-item-title class="font-weight-bold text-subtitle-1">
                  {{ currentUser.display_name }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span class="text-caption">
                    {{ currentUser.login }} ({{ currentUser.user_type }})
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <v-divider />
          <v-list-item @click="_ => toggleDarkMode()">
            <v-list-item-action>
              <v-icon>{{ buttonDarkModeText[0] }}</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>Use {{ buttonDarkModeText[1] }} Mode</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item :to="{ name: 'user-account' }">
            <v-list-item-action><v-icon>account_circle</v-icon></v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>User Account</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            href="https://innovationlawlab.org/docs/"
            target="_blank"
          >
            <v-list-item-action>
              <v-icon>help</v-icon>
            </v-list-item-action>

            <v-list-item-content>
              <v-list-item-title>Documentation</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            href="https://innovationlawlab.org/technical-support/"
            target="_blank"
          >
            <v-list-item-action>
              <v-icon>question_answer</v-icon>
            </v-list-item-action>

            <v-list-item-content>
              <v-list-item-title>Technical Support</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider />
          <v-list-item @click="_ => signOut()">
            <v-list-item-action>
              <v-icon>exit_to_app</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>Sign Out</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>
    <v-form
      ref="form"
      v-model="search.formValid"
      accept-charset="UTF-8"
      class="d-block d-sm-none"
      action="/search"
      method="get"
      @submit="checkSearchForm"
    >
      <v-text-field
        v-model="search.term"
        class="mt-16 ml-16 mr-2 mb-n16"
        append-icon="search"
        dense
        label="Global Search"
        name="term"
        :rules="search.formRules"
        single-line
        solo
        text
        @focusout="resetFormValidation"
      />
    </v-form>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import { mapFields } from 'vuex-map-fields'

import { alertHandler } from '@/alertHandler'
import { dateTimeDisplayFormat } from '@/utilities'
import { rules } from '@/utilities/validation-rules'

import CalendarSyncStatusButton from '@/components/Calendar/CalendarSyncStatusButton.vue'
import LawLabIcon from '@/components/Icons/LawLabIcon.vue'
import NotificationsBox from '@/components/Nav/NotificationsBox.vue'
import TmetricBox from '@/components/Nav/TmetricBox.vue'

export default {
  name: 'NavBar',

  components: {
    CalendarSyncStatusButton,
    LawLabIcon,
    NotificationsBox,
    TmetricBox,
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      appHash: process.env.VUE_APP_GIT_HASH,
      appTag: process.env.VUE_APP_GIT_TAG,
      search: {
        formRules: [],
        formValid: false,
        term: '',
      },
    }
  },

  computed: {
    ...mapFields([
      'currentUser',
    ]),

    avatar () {
      return (
        (this.currentUser.first_name && this.currentUser.first_name.length ? this.currentUser.first_name[0] : '') +
        (this.currentUser.last_name && this.currentUser.last_name ? this.currentUser.last_name[0] : '')
      )
    },

    buttonDarkModeText () {
      return !this.$vuetify.theme.dark ? ['mdi-weather-night', 'Dark'] : ['mdi-brightness-5', 'Light']
    },
  },

  async created () {
    this.search.formRules = [
      v => !!v || 'A search term is required',
      rules.searchTermMin,
    ]
  },

  mounted () {
    const theme = localStorage.getItem('dark_theme')
    if (theme) {
      if (theme === 'true') this.$vuetify.theme.dark = true
      else this.$vuetify.theme.dark = false
    } else if (
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      this.$vuetify.theme.dark = true
      localStorage.setItem(
        'dark_theme',
        this.$vuetify.theme.dark.toString()
      )
    }
  },

  methods: {
    checkSearchForm (e) {
      if (this.search.formValid && this.$route.path === '/search') {
        // change URL to reflect new search, which triggers watcher in Search.vue,
        // without re-loading the page
        if (this.search.term !== this.$route.query.term) this.$router.push('/search?term=' + this.search.term)

        e.preventDefault()
      }
      if (!this.search.formValid) e.preventDefault()
    },

    dateTimeDisplayFormat,

    goToSettings () {
      if (this.$route.name !== 'settings') this.$router.push({ name: 'settings' })
    },

    goToUserAccount () {
      if (this.$route.name !== 'user-account') this.$router.push({ name: 'user-account' })
    },

    resetFormValidation () {
      this.$refs.form.resetValidation()
    },

    signOut () {
      // because this log out is user-triggered,
      // remove saved page to not be returned to upon login
      window.sessionStorage.removeItem('returnTo')

      // to support logging out from all windows
      window.localStorage.setItem('logout', Date.now())
      this.logoutUserModel().then(_ => {
        if (this.$route.name !== 'login') this.$router.push('/login')
      })
    },

    toggleDarkMode: function () {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark
      localStorage.setItem('dark_theme', this.$vuetify.theme.dark.toString())
    },

    toggleMenu () {
      this.menuOpened = !this.menuOpened
      this.$emit('toggle-app-drawer')
    },

    ...mapActions([
      'logoutUserModel',
    ]),

    ...mapMutations([
      'setCurrentUser',
    ]),
  },
}
</script>
